/**
 * Otto Agent → creation-form draft handoff.
 *
 * When Otto finishes gathering data it produces a fully-formed draft. Rather
 * than thread a big object through react-router state (which a hard navigation
 * would drop), we stash it in `sessionStorage` under a one-shot nonce and put
 * only `?draft=<nonce>` on the URL. The destination page reads + DELETES it
 * (single-use), so a refresh can't silently re-apply a stale draft.
 *
 * The parent Otto surface WRITES via {@link stashOttoDraft}; the CRM generate /
 * campaign pages READ via {@link consumeOttoDraft}. Draft-type-agnostic — the
 * invoice/quote draft and the campaign draft both ride this channel; each
 * destination validates the shape it expects.
 */
const KEY_PREFIX = 'otto_draft_';

/** Stash a draft and return the nonce to put on the URL (`?draft=<nonce>`). */
export function stashOttoDraft(draft: unknown): string {
  const nonce = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    sessionStorage.setItem(KEY_PREFIX + nonce, JSON.stringify(draft));
  } catch {
    /* sessionStorage unavailable (private mode / quota) — caller falls back */
  }
  return nonce;
}

/**
 * Read + remove the stashed draft for `nonce` (single-use). Returns the parsed
 * value, or `null` when the nonce is missing / already consumed / unparseable.
 */
export function consumeOttoDraft(nonce: string | null | undefined): unknown | null {
  if (!nonce) return null;
  const key = KEY_PREFIX + nonce;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(key);
    if (raw != null) sessionStorage.removeItem(key);
  } catch {
    return null;
  }
  if (raw == null) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}
